import { useCallback, useMemo } from "react";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

/**
 * Generates a hex color code from a string using a hashing algorithm.
 * @param str - The input string to hash
 * @returns A hexadecimal color string (e.g. '#a1b2c3')
 */
const stringToColor = (str: string): string => {
  // Return default color for empty string
  if (str.length === 0) {
    return "#000000";
  }

  // Create hash using reduce
  const hash: number = [...str].reduce((acc: number, char: string) => {
    const code: number = char.charCodeAt(0);
    return ((acc << 5) - acc + code) & 0xffffffff;
  }, 0);

  // Map hash to R, G, B components using functional operations
  const rgbComponents: string[] = [0, 8, 16].map((offset: number): string =>
    ((hash >> offset) & 0xff).toString(16).padStart(2, "0")
  );

  // Combine components to form hex color string
  return `#${rgbComponents.join("")}`;
};

/**
 * Represents the type of color component to shift
 */
type ShiftType = "hue" | "saturation" | "lightness";

/**
 * Shifts a hex color by a specified amount in the HSL color space.
 * @param hexColor - Hex color code (with or without # prefix)
 * @param shiftValue - Value between -1 and 1 representing shift amount
 * @param shiftType - What to shift: 'hue', 'saturation', or 'lightness'
 * @returns Shifted hex color code with # prefix
 */
function shiftColor(
  hexColor: string,
  shiftValue: number,
  shiftType: ShiftType = "hue"
): string {
  // Ensure shift value is within range -1 to 1
  shiftValue = Math.max(-1, Math.min(1, shiftValue));

  // Remove # if present
  hexColor = hexColor.replace(/^#/, "");

  // Parse the hex color to RGB
  const r: number = parseInt(hexColor.substring(0, 2), 16) / 255;
  const g: number = parseInt(hexColor.substring(2, 4), 16) / 255;
  const b: number = parseInt(hexColor.substring(4, 6), 16) / 255;

  // Convert RGB to HSL
  const max: number = Math.max(r, g, b);
  const min: number = Math.min(r, g, b);
  let h: number = 0;
  let s: number = 0;
  let l: number = (max + min) / 2;

  if (max !== min) {
    const d: number = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  // Apply shift based on shiftType
  switch (shiftType.toLowerCase() as ShiftType) {
    case "hue":
      // Shift hue (0-1) and ensure it wraps around properly
      h = (h + shiftValue) % 1;
      if (h < 0) {
        h += 1;
      }
      break;
    case "saturation":
      // Shift saturation (0-1) and clamp
      s = Math.max(0, Math.min(1, s + shiftValue));
      break;
    case "lightness":
      // Shift lightness (0-1) and clamp
      l = Math.max(0, Math.min(1, l + shiftValue));
      break;
    default:
      throw new Error("shiftType must be 'hue', 'saturation', or 'lightness'");
  }

  // Convert HSL back to RGB
  let r1: number = 0;
  let g1: number = 0;
  let b1: number = 0;

  if (s === 0) {
    r1 = g1 = b1 = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };

    const q: number = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p: number = 2 * l - q;

    r1 = hue2rgb(p, q, h + 1 / 3);
    g1 = hue2rgb(p, q, h);
    b1 = hue2rgb(p, q, h - 1 / 3);
  }

  // Convert RGB to hex
  const toHex = (x: number): string => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

/**
 * Normalizes a string by converting it to lowercase, trimming whitespace,
 * and replacing spaces with underscores. It also removes special characters.
 * @param input - The input string to normalize
 * @returns The normalized string
 */
function normalizeString(input: string): string {
  if (!input) {
    return "";
  }

  return input
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/-+/g, "-"); // Replace multiple hyphens with single hyphen
}

/**
 * Returns a hook that allows the user to get a color based on a string.
 * The color is generated using a hashing algorithm and can be shifted in the HSL color space.
 * The hook uses the theme colors for specific asset categories.
 * @returns A function that takes a string and returns a color
 * @example
 * const getColor = useAssetCategoryColors();
 * const color = getColor("equity"); // returns a color based on the string "equity"
 */
const useAssetCategoryColors = (): ((label: string) => string) => {
  const { color } = useTheme();

  const map: Record<string, string> = useMemo(
    () => ({
      equity: color["$chartSegmentOne"],
      "fixed income": color["$chartSegmentTwo"],
      cash: color["$chartSegmentThree"],
      other: color["$chartSegmentFour"],
      alternative: color["$chartSegmentFive"],
      balanced: color["$chartSegmentSix"],
    }),
    [color]
  );

  return useCallback(
    (str: string): string => {
      const normalizedStr = normalizeString(str);
      const fixed = map[normalizedStr];
      const color = fixed || stringToColor(normalizedStr);
      const shiftedColor = shiftColor(color, 0, "hue");
      return shiftedColor;
    },
    [map]
  );
};

export default useAssetCategoryColors;
